import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

import { useVersions, useAdminAccess } from '../../store/contentAdminStore';
import type { ContentVersion } from '../../services/contentSnapshotService';

// Modal para criar nova versão
interface CreateVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (description: string, createdBy: string) => Promise<void>;
  isLoading: boolean;
}

const CreateVersionModal: React.FC<CreateVersionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [description, setDescription] = useState('');
  const [createdBy, setCreatedBy] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (description.trim() && createdBy.trim()) {
      await onSubmit(description.trim(), createdBy.trim());
      setDescription('');
      setCreatedBy('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Criar Nova Versão
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Atualização do questionário v2.1"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Criado por
            </label>
            <input
              type="text"
              value={createdBy}
              onChange={(e) => setCreatedBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Seu nome"
              required
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !description.trim() || !createdBy.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Criando...' : 'Criar Versão'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Modal de confirmação para ações destrutivas
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  isDestructive?: boolean;
  isLoading: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  isDestructive = false,
  isLoading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
      >
        <div className="flex items-center gap-3 mb-4">
          {isDestructive ? (
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          ) : (
            <CheckCircleIcon className="w-6 h-6 text-blue-600" />
          )}
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        
        <p className="text-gray-700 mb-6">{message}</p>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-white rounded-md transition-colors ${
              isDestructive
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Processando...' : confirmText}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Componente principal
export const ContentVersionManager: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'activate' | 'delete' | 'duplicate';
    version: ContentVersion;
    isOpen: boolean;
  } | null>(null);

  const {
    versions,
    isLoading,
    error,
    fetchVersions,
    createVersion,
    activateVersion,
    deleteVersion,
    duplicateVersion,
  } = useVersions();

  const { hasAdminAccess, checkAdminAccess } = useAdminAccess();

  // Carrega dados na montagem
  useEffect(() => {
    const initializeData = async () => {
      await checkAdminAccess();
      if (hasAdminAccess !== false) {
        await fetchVersions();
      }
    };

    initializeData();
  }, []);

  // Handlers para ações
  const handleCreateVersion = async (description: string, createdBy: string) => {
    await createVersion(description, createdBy);
  };

  const handleActivateVersion = async (version: ContentVersion) => {
    setConfirmAction({
      type: 'activate',
      version,
      isOpen: true,
    });
  };

  const handleDeleteVersion = async (version: ContentVersion) => {
    setConfirmAction({
      type: 'delete',
      version,
      isOpen: true,
    });
  };

  const handleDuplicateVersion = async (version: ContentVersion) => {
    setConfirmAction({
      type: 'duplicate',
      version,
      isOpen: true,
    });
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      switch (confirmAction.type) {
        case 'activate':
          await activateVersion(confirmAction.version.id);
          break;
        case 'delete':
          await deleteVersion(confirmAction.version.id);
          break;
        case 'duplicate':
          await duplicateVersion(
            confirmAction.version.id,
            `Copy of ${confirmAction.version.description}`,
            'Admin'
          );
          break;
      }
    } finally {
      setConfirmAction(null);
    }
  };

  // Formatação de data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Renderização de status
  const renderStatusBadge = (version: ContentVersion) => {
    if (version.is_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          <CheckCircleIcon className="w-3 h-3" />
          Ativa
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
        <ClockIcon className="w-3 h-3" />
        Draft
      </span>
    );
  };

  // Verificação de acesso
  if (hasAdminAccess === false) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Negado</h3>
        <p className="text-gray-600">
          Você precisa de permissões administrativas para acessar o gerenciamento de conteúdo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gerenciamento de Versões
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie versões do conteúdo de avaliação e questões
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Nova Versão
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && versions.length === 0 && (
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="flex gap-2">
                  <div className="h-8 bg-gray-200 rounded w-20"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Versions Table */}
      {!isLoading && versions.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Versão
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criado por
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <AnimatePresence>
                  {versions.map((version) => (
                    <motion.tr
                      key={version.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {version.version}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {version.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {renderStatusBadge(version)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {version.created_by}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(version.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* View/Edit */}
                          <Link
                            to={`/admin/content/versions/${version.id}`}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Visualizar"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </Link>

                          {/* Edit */}
                          <Link
                            to={`/admin/content/versions/${version.id}/edit`}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Link>

                          {/* Activate */}
                          {!version.is_active && (
                            <button
                              onClick={() => handleActivateVersion(version)}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Ativar"
                            >
                              <PlayIcon className="w-4 h-4" />
                            </button>
                          )}

                          {/* Duplicate */}
                          <button
                            onClick={() => handleDuplicateVersion(version)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Duplicar"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          </button>

                          {/* Delete */}
                          {!version.is_active && (
                            <button
                              onClick={() => handleDeleteVersion(version)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && versions.length === 0 && (
        <div className="text-center py-12">
          <DocumentDuplicateIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nenhuma versão encontrada
          </h3>
          <p className="text-gray-600 mb-4">
            Comece criando sua primeira versão de conteúdo.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Criar Primeira Versão
          </button>
        </div>
      )}

      {/* Modais */}
      <CreateVersionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateVersion}
        isLoading={isLoading}
      />

      {confirmAction && (
        <ConfirmModal
          isOpen={confirmAction.isOpen}
          onClose={() => setConfirmAction(null)}
          onConfirm={executeConfirmAction}
          title={
            confirmAction.type === 'activate'
              ? 'Ativar Versão'
              : confirmAction.type === 'delete'
              ? 'Excluir Versão'
              : 'Duplicar Versão'
          }
          message={
            confirmAction.type === 'activate'
              ? `Tem certeza que deseja ativar a versão "${confirmAction.version.version}"? Isso irá desativar a versão atual.`
              : confirmAction.type === 'delete'
              ? `Tem certeza que deseja excluir a versão "${confirmAction.version.version}"? Esta ação não pode ser desfeita.`
              : `Tem certeza que deseja duplicar a versão "${confirmAction.version.version}"?`
          }
          confirmText={
            confirmAction.type === 'activate'
              ? 'Ativar'
              : confirmAction.type === 'delete'
              ? 'Excluir'
              : 'Duplicar'
          }
          isDestructive={confirmAction.type === 'delete'}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};